/**
 * Animated horizontal tabs, ported from entirefeed's `animated-tabs.tsx`: a
 * soft pill that glides between triggers as the pointer moves over them, and
 * a sprung underline that follows the active tab.
 *
 * Rebuilt on Base UI — this workspace's tabs primitive — with CSS transitions
 * in place of framer-motion, so it adds no dependency and speaks the theme's
 * tokens rather than hard-coded neutrals. `Tabs.Indicator` supplies the
 * active tab's geometry as CSS variables; the hover pill is the one piece we
 * measure ourselves.
 *
 * On a narrow page the strip scrolls sideways instead of squeezing: an edge
 * with tabs hidden behind it fades out, and the active tab is scrolled into
 * view whenever it changes or the strip resizes (see `tabStripScroll`).
 *
 * @module components/ui/animated-tabs
 */
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "~/lib/utils";

import { tabStripEdges, tabStripScrollToReveal, type TabStripEdges } from "./tabStripScroll";

/** The pill's geometry and how it should get there. */
interface HoverPill {
  readonly left: number;
  readonly width: number;
  readonly visible: boolean;
  /** Snap into place (first hover) rather than glide from the last spot. */
  readonly jump: boolean;
}

interface AnimatedTabsContextValue {
  readonly listRef: React.RefObject<HTMLDivElement | null>;
  readonly pill: HoverPill;
  readonly hoverTrigger: (element: HTMLElement) => void;
  readonly leaveList: () => void;
}

const AnimatedTabsContext = createContext<AnimatedTabsContextValue | null>(null);

function useAnimatedTabs(): AnimatedTabsContextValue {
  const context = useContext(AnimatedTabsContext);
  if (context === null) {
    throw new Error("AnimatedTabs parts must be used within <AnimatedTabs>.");
  }
  return context;
}

const SPRING = "cubic-bezier(0.22, 1, 0.36, 1)";

/** How wide an edge fade is, and the room left beside a tab scrolled into view. */
const EDGE_FADE_PX = 40;

const NO_EDGES: TabStripEdges = { start: false, end: false };

export function AnimatedTabs({
  className,
  ...props
}: ComponentProps<typeof BaseTabs.Root>) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [pill, setPill] = useState<HoverPill>({
    left: 0,
    width: 0,
    visible: false,
    jump: true,
  });

  const hoverTrigger = useCallback((element: HTMLElement) => {
    const list = listRef.current;
    if (list === null) return;
    const listRect = list.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    setPill((previous) => ({
      // The pill lives in the strip's scrolled content, so a scrolled strip
      // offsets it by the distance scrolled.
      left: rect.left - listRect.left + list.scrollLeft,
      width: rect.width,
      visible: true,
      // The first hover materialises in place; later ones glide over.
      jump: !previous.visible,
    }));
  }, []);

  const leaveList = useCallback(() => {
    setPill((previous) => (previous.visible ? { ...previous, visible: false } : previous));
  }, []);

  return (
    <AnimatedTabsContext.Provider value={{ listRef, pill, hoverTrigger, leaveList }}>
      <BaseTabs.Root className={cn("flex min-h-0 flex-col gap-2", className)} {...props} />
    </AnimatedTabsContext.Provider>
  );
}

export function AnimatedTabsList({
  className,
  children,
  noBorder = false,
  ...props
}: ComponentProps<typeof BaseTabs.List> & {
  /** Drop the hairline under the row when the list sits on its own divider. */
  readonly noBorder?: boolean;
}) {
  const { listRef, pill, leaveList } = useAnimatedTabs();
  const [edges, setEdges] = useState<TabStripEdges>(NO_EDGES);

  useEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    const measure = () => {
      const next = tabStripEdges(list);
      setEdges((previous) =>
        previous.start === next.start && previous.end === next.end ? previous : next,
      );
    };
    const reveal = (behavior: ScrollBehavior) => {
      const tab = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (tab === null) return;
      const listRect = list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const left = tabStripScrollToReveal({
        itemLeft: tabRect.left - listRect.left + list.scrollLeft,
        itemWidth: tabRect.width,
        scrollLeft: list.scrollLeft,
        clientWidth: list.clientWidth,
        scrollWidth: list.scrollWidth,
        margin: EDGE_FADE_PX,
      });
      if (left !== null) list.scrollTo({ left, behavior });
    };
    const settle = () => {
      measure();
      reveal("auto");
    };
    settle();
    // The strip resizes with the page, and its content grows as tab counts load.
    const resize = new ResizeObserver(settle);
    resize.observe(list);
    for (const child of Array.from(list.children)) resize.observe(child);
    const selection = new MutationObserver(() =>
      reveal(window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"),
    );
    selection.observe(list, { subtree: true, attributeFilter: ["aria-selected"] });
    list.addEventListener("scroll", measure, { passive: true });
    return () => {
      resize.disconnect();
      selection.disconnect();
      list.removeEventListener("scroll", measure);
    };
  }, [listRef]);

  const pillStyle: CSSProperties = {
    left: pill.left,
    width: pill.width,
    opacity: pill.visible ? 1 : 0,
    transitionProperty: "left, width, opacity",
    transitionDuration: pill.jump ? "0ms, 0ms, 200ms" : "400ms, 400ms, 300ms",
    transitionTimingFunction: `${SPRING}, ${SPRING}, ease-in-out`,
  };

  return (
    <div className={cn("relative", !noBorder && "border-b border-border/60")}>
      <BaseTabs.List
        ref={listRef}
        // Bottom padding is the gap between the hover/active pill and the
        // underline: without it the pill's edge sat on the indicator and the
        // two read as one smeared shape.
        // Scrolls without a scrollbar: the faded edge is the affordance.
        className={cn(
          "relative -mx-3 flex overflow-x-auto pb-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "[--fade-size:40px]",
          edges.start && "mask-l-from-[calc(100%-var(--fade-size))]",
          edges.end && "mask-r-from-[calc(100%-var(--fade-size))]",
          className,
        )}
        data-overflow-start={edges.start || undefined}
        data-overflow-end={edges.end || undefined}
        onMouseLeave={leaveList}
        {...props}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 z-0 h-[calc(100%-0.375rem)] rounded-lg bg-accent/80 motion-reduce:transition-none"
          style={pillStyle}
        />
        <div className="relative z-[1] flex gap-2">{children}</div>
        <BaseTabs.Indicator
          className={cn(
            "absolute bottom-0 z-[2] h-0.5 rounded-full bg-primary",
            "transition-[left,width] duration-300 motion-reduce:transition-none",
          )}
          style={{
            // Inset the line by the trigger's horizontal padding so it
            // underlines the label, not the hit area — and, with the list
            // pulled left by that same padding, the first label and its line
            // sit on the page's left gridline.
            left: "calc(var(--active-tab-left) + 12px)",
            width: "calc(var(--active-tab-width) - 24px)",
            transitionTimingFunction: SPRING,
          }}
        />
      </BaseTabs.List>
    </div>
  );
}

export function AnimatedTabsTrigger({
  className,
  children,
  onMouseEnter,
  ...props
}: ComponentProps<typeof BaseTabs.Tab>) {
  const { hoverTrigger } = useAnimatedTabs();

  return (
    <BaseTabs.Tab
      className={cn(
        "flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2",
        "text-sm font-medium text-muted-foreground transition-colors duration-300",
        "hover:text-foreground data-[active]:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        className,
      )}
      onMouseEnter={(event) => {
        hoverTrigger(event.currentTarget);
        onMouseEnter?.(event);
      }}
      {...props}
    >
      {children}
    </BaseTabs.Tab>
  );
}

/**
 * Base UI unmounts inactive panels, so the reveal runs once per activation:
 * the panel's content lifts in from a hair below, the way the original's
 * `animate-in` did, without a keyframe sheet.
 */
function Reveal({ children }: { readonly children: ReactNode }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div
      className={cn(
        "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
        shown ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
      )}
    >
      {children}
    </div>
  );
}

export function AnimatedTabsContent({
  className,
  children,
  ...props
}: ComponentProps<typeof BaseTabs.Panel>) {
  return (
    <BaseTabs.Panel className={cn("min-h-0 flex-1 outline-none", className)} {...props}>
      <Reveal>{children}</Reveal>
    </BaseTabs.Panel>
  );
}
